import { graphql } from '@packages/gql/generated/shopping';
import type { CriteoPageId } from '@packages/gql/generated/shopping/graphql';
import { useDeviceType } from '@packages/shared';
import { useCookies } from '@packages/utilities';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { RecoProductCardSkeleton } from '../../RecoProductCardSkeleton';
import { useRecoElement } from '../../../helpers/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type CriteoProductsRecoSliderProps = Pick<
  RequestParams,
  'available' | 'productId' | 'price'
> &
  Omit<ProductsRecoSliderProps, 'preferTitle'>;

const CriteoProductsDocument = graphql(/* GraphQL */ `
  query CriteoProducts(
    $available: Int!
    $pageId: CriteoPageId!
    $price: Float!
    $productId: String!
  ) {
    criteoViewItem(
      params: { productId: $productId, available: $available, pageId: $pageId, price: $price }
    ) {
      type
      recommendations {
        ...ProductRecommendationFragment
        product {
          akl
        }
      }
      placementBeacons {
        url
        purpose
      }
    }
  }
`);

const RDE_TYPE: RdeType = 'criteoViewItem';

/**
 * CriteoProductsRecoSlider component description displayed in storybook
 * */
export const CriteoProductsRecoSlider: FC<CriteoProductsRecoSliderProps> = ({
  available,
  elementIndex = 0,
  fetchOnThreshold = false,
  pageTemplate,
  productId,
  price,
  title,
}) => {
  const { isMobile } = useDeviceType();
  const { isInThreshold, isInView, recoEl } = useRecoElement();
  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const isApp = appView === 'true';

  const pageId: CriteoPageId = (() => {
    if (isApp) return 'App';
    if (isMobile) return 'Mobile';
    return 'Desktop';
  })();

  const [{ data, error }] = useQuery({
    query: CriteoProductsDocument,
    variables: {
      available,
      productId,
      pageId,
      price,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
  });

  const recommendations = data ? data.criteoViewItem.recommendations : undefined;
  const placementBeacons = data ? data.criteoViewItem.placementBeacons : undefined;

  const rdeTypeAbbr = data?.criteoViewItem.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    placementBeacons,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations,
    title,
    trackingCondition: isInView && !!recommendations && recommendations.length > 0,
  });

  if ((error && !recommendations) || (recommendations && recommendations.length === 0)) {
    return null;
  }

  return (
    <RecoCarousel title={title} ref={recoEl} loadingState={!recommendations}>
      {!recommendations
        ? // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          [...Array(3)].map((_, i) => <RecoProductCardSkeleton key={i} />)
        : recommendations
            .filter((recommendation) => recommendation.product)
            .map((recommendation, idx) => (
              <RecoProductCard
                pageTemplate={pageTemplate}
                // rule is disabled because order doesn't change between rerenders
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                elementIndex={elementIndex}
                position={idx}
                recommendationData={recommendation}
                productsLength={recommendations.length}
                rdeType={RDE_TYPE}
              />
            ))}
    </RecoCarousel>
  );
};
